import merge from "lodash/merge";
import { createSelector } from "reselect";
import { DEFAULT_DCUI_CONTENT, DEFAULT_DCUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { DCUITheme } from "@iddataweb/idw-common-ui/types";
import { ConfigurationResponse, ConfigurationResponseProperties } from "@idw-types/data";
import { ApplicationState, VerificationConfigurationState } from "@idw-types/state";
import { isNullOrUndefined } from "@utils/object-util";

export const getVerificationConfigurationState = (state: ApplicationState): VerificationConfigurationState => {
	return state.verificationConfiguration;
};

export const getVerificationConfiguration = createSelector(
	getVerificationConfigurationState,
	(state: VerificationConfigurationState): Partial<ConfigurationResponse> => {
		return state.config || {};
	});

export const getAsi = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.asi || null;
});

export const getAPIKey = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.apikey;
});

export const getConfigurationResponseProperties = createSelector(getVerificationConfiguration, (configuration): Partial<ConfigurationResponseProperties> => {
	return configuration.properties || {};
});

export const getDCUIHeaderBGColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiHeaderBGColor || "#0277bd";
});

export const getDCUIHeaderTextColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiHeaderTextColor || "#fff";
});

export const getDCUIFooterBGColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiFooterBGColor || "#0277bd";
});

export const getDCUIFooterTextColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiFooterTextColor || "#4fc3f7";
});

export const getDCUIFooterText = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiFooterText || `© ${new Date().getFullYear()} - IDDataWeb`;
});

export const getDCUIPrimaryBtnColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiPrimaryBtnColor || "#0277bd";
});

export const getDCUIPrimaryBtnTextColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiPrimaryBtnTextColor || "#fff";
});

export const getDCUISecondaryBtnColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiPrimaryBtnColor || "#3f51b5";
});

export const getDCUISecondaryBtnTextColor = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiPrimaryBtnTextColor || "#fff";
});

export const getDCUIRPName = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiRPName || "IDDataweb";
});

export const getDCUIRPLogoUrl = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.dcuiRPLogoUrl || null;
});

export const getConfigurationTheme = createSelector(getConfigurationResponseProperties, (properties): DCUITheme => {
	const theme = properties.theme;
	if (theme && Object.keys(theme).length > 0) {
		return merge({}, DEFAULT_DCUI_THEME, theme);
	}
	return DEFAULT_DCUI_THEME;
});

export const getConfigurationContent = createSelector(
	getConfigurationResponseProperties,
	(properties: Partial<ConfigurationResponseProperties>) => {
		const content = properties.content;
		if (content && Object.keys(content).length > 0) {
			return content;
		}
		return DEFAULT_DCUI_CONTENT;
	}
);

export const getGeneralTheme = createSelector(getConfigurationTheme, (theme: DCUITheme) => {
	return theme.general;
});

export const getHeaderTheme = createSelector(getConfigurationTheme, (theme: DCUITheme) => {
	return theme.header;
});

export const getFooterTheme = createSelector(getConfigurationTheme, (theme: DCUITheme) => {
	return theme.footer;
});

export const getLanguage = createSelector(getVerificationConfiguration, (configuration) => {
	return configuration.language || null;
});

export const getLocalizeStrings = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.localizeStrings || false;
});

export const getMergeThemingAndLocalizedFile = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.mergeThemingAndLocalizedFile || false;
});

export const getLocalizeFileNamespace = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.localizeFileNamespace || null;
});

export const getThemeTextColor = createSelector(getConfigurationTheme, (theme: DCUITheme) => {
	return theme.general.mainTextColor;
});

export const getAvailableLanguages = createSelector(getConfigurationResponseProperties, (properties) => {
	const languageObj = properties.availableLanguages || {};
	const list = Object.keys(languageObj).map((languageCode: LocaleTypes) => {
		return { code: languageCode, name: languageObj[languageCode] };
	});
	return list;
});

export const getUseAcuantSDKCapture = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.useAcuantSDKCapture || false;
});

export const getUseAcuantSDKDPIValidation = createSelector(getConfigurationResponseProperties, (properties) => {
	return !isNullOrUndefined(properties.useAcuantSDKDPIValidation) ? properties.useAcuantSDKDPIValidation : true;
});

export const getUseAcuantSDKGlareValidation = createSelector(getConfigurationResponseProperties, (properties) => {
	return !isNullOrUndefined(properties.useAcuantSDKGlareValidation) ? properties.useAcuantSDKGlareValidation : true;
});

export const getUseAcuantSDKSharpnessValidation = createSelector(getConfigurationResponseProperties, (properties) => {
	return !isNullOrUndefined(properties.useAcuantSDKSharpnessValidation) ? properties.useAcuantSDKSharpnessValidation : true;
});

export const getLivenessCheck = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.livenessCheck || false;
});

export const getAcuantSDKGlareThreshold = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.acuantSDKGlareThreshold || 50;
});

export const getAcuantSDKDPIThreshold = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.acuantSDKDPIThreshold || 50;
});

export const getAcuantSDKSharpnessThreshold = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.acuantSDKSharpnessThreshold || 50;
});

export const getAcuantDisableCamera = createSelector(getConfigurationResponseProperties, (properties) => {
	return properties.acuantDisableCamera || false;
});


export const getTmxInDCUI = createSelector(getConfigurationResponseProperties, (properties): boolean => {
	return properties.tmxInDCUI || null;
});

export const getTMXIDWJS = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxIDWJS || null;
});

export const getTMXSessionId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmx_sessionid || null;
});

export const getTMXOrgId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxOrgID || null;
});

export const getTMXPageId = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxPageID || null;
});

export const getTMXProfilingDomain = createSelector(getConfigurationResponseProperties, (properties): string => {
	return properties.tmxProfilingDomain || null;
});